import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useExceptionItemActions } from "./ducks/hooks/action";
import { useExceptionItems } from "./ducks/hooks/state";
import { ExceptionItem } from "./ducks/type";
import { CreateModal } from "../modal/modal";

export function TabsComponent() {
  const [activeTab, setActiveTab] = useState("ncm");
  const items = useExceptionItems();
  const { add, remove } = useExceptionItemActions();
  const { handleModal } = useDialogModal();

  const openModalCreate = (type: "NCM" | "Produto") => {
    handleModal({
      isOpen: true,
      title: `Cadastrar novo ${type}`,
      description: `Preencha as informações abaixo`,
      element: (
        <CreateModal
          type={type}
          onSave={(data: { ncm?: string; sku?: string; description: string }) => {
            add({ type, data });
            handleModal({ isOpen: false });
          }}
        />
      ),
    });
  };

  const isNcmDisabled = items.some((item: ExceptionItem) => item.type === "Produto");
  const isProductDisabled = items.some((item: ExceptionItem) => item.type === "NCM");

  return (
    <Card className="w-full border border-gray-200/80 bg-white backdrop-blur-sm shadow-lg transition-all duration-200 hover:shadow-xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-gray-900">
              NCM e Produtos
            </CardTitle>
            <CardDescription>
              Adicione os códigos NCM ou produtos relacionados
            </CardDescription>
          </div>

          <Button
            type="button"
            onClick={() =>
              openModalCreate(activeTab === "ncm" ? "NCM" : "Produto")
            }
            variant="default"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {activeTab === "ncm" ? "Adicionar NCM" : "Adicionar Produto"}
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs
          defaultValue="ncm"
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="flex space-x-2 bg-muted w-40 rounded-md p-1 mb-4">
            <TabsTrigger
              value="ncm"
              disabled={isNcmDisabled}
              className="px-4 py-2 text-sm text-gray-700 font-semibold data-[state=active]:bg-amber-400 data-[state=active]:text-black rounded-md transition-all"
            >
              NCM
            </TabsTrigger>
            <TabsTrigger
              value="product"
              disabled={isProductDisabled}
              className="px-4 py-2 text-sm text-gray-700 font-semibold data-[state=active]:bg-amber-400 data-[state=active]:text-black rounded-md transition-all"
            >
              Produto
            </TabsTrigger>
          </TabsList>

          <div className="w-full bg-white p-6 rounded-lg shadow-lg max-h-[360px] overflow-y-auto">
            <TabsContent value="ncm" className="w-full mt-4 bg-white">
              {items.filter((item: ExceptionItem) => item.type === "NCM").length > 0 ? (
                <>
                  <div className="grid grid-cols-[200px_1fr_auto] font-semibold text-gray-600 border-b pb-2 mb-2">
                    <span className="border-r pr-4 border-gray-300">NCM</span>
                    <span className="pl-4">Descrição</span>
                    <span className="text-right pr-2">Ações</span>
                  </div>

                  {items
                    .filter((item: ExceptionItem) => item.type === "NCM")
                    .map((item: ExceptionItem, index: number) => (
                      <div
                        key={index}
                        className="grid grid-cols-[200px_1fr_auto] border-b py-2 items-center"
                      >
                        <span className="text-gray-700 truncate border-r pr-4 border-gray-200">
                          {item.data.ncm === "all" ? "TODOS" : item.data.ncm}
                        </span>
                        <span className="text-gray-500 pl-4">
                          {item.data.description}
                        </span>
                        <button
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                </>
              ) : (
                <div className="py-4 text-center text-gray-400">
                  Nenhum NCM cadastrado.
                </div>
              )}
            </TabsContent>

            <TabsContent value="product" className="w-full mt-4 bg-white">
              {items.filter((item: ExceptionItem) => item.type === "Produto").length > 0 ? (
                <>
                  <div className="grid grid-cols-[200px_1fr_auto] font-semibold text-gray-600 border-b pb-2 mb-2">
                    <span className="border-r pr-4 border-gray-300">SKU</span>
                    <span className="pl-4">Descrição</span>
                    <span className="text-right pr-2">Ações</span>
                  </div>

                  {items
                    .filter((item: ExceptionItem) => item.type === "Produto")
                    .map((item: ExceptionItem, index: number) => (
                      <div
                        key={index}
                        className="grid grid-cols-[200px_1fr_auto] border-b py-2 items-center"
                      >
                        <span className="text-gray-700 truncate border-r pr-4 border-gray-200">
                          {item.data.sku === "all" ? "TODOS" : item.data.sku}
                        </span>
                        <span className="text-gray-500 pl-4">
                          {item.data.description}
                        </span>
                        <button
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                </>
              ) : (
                <div className="py-4 text-center text-gray-400">
                  Nenhum SKU cadastrado.
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
}
